/**
 * Break sheet printing.
// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
 *
 * Two kinds of sheet, because operators do two different things. A plain sheet
 * says only "a new document starts here" and carries no values, so every plain
 * sheet is identical and a stack of them printed once lasts indefinitely — that
 * is the pile that lives next to the scanner. A field-carrying sheet is prepared
 * for one specific document that is about to be scanned, and the values it shows
 * are stored against the identifier its barcode carries.
 *
 * The barcode holds only that identifier. That is what lets the values be
 * unlimited in number and length while the symbol stays small enough to print
 * nine times across the sheet in four orientations.
 *
 * PREPARING A LARGE SET
 *
 * A hundred prepared sheets is an hour of typing, and this dialog used to hold
 * all of it in component state and write nothing until Print. Three separate
 * ways out — Escape, the backdrop, Close — threw it away without asking. So:
 * the set can be imported from a table the values already live in rather than
 * typed at all; whatever is typed is written to a per-job draft continuously and
 * restored on the way back in; and every exit stops to ask. The 25-sheet cap
 * that used to bound the loss is gone, because the loss is gone.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useModalFocus } from './use-modal-focus';
import { isTopVisibleDialog } from './dialog-tab-stops';
import { TooltipButton } from './TooltipButton';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  FileSpreadsheet,
  FileText,
  LoaderCircle,
  Plus,
  Printer,
  RotateCcw,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react';
import type { BreakSheetPaper, BreakSheetRun, JobDefinition } from '../shared/desktop-api';
import {
  carrySheetToType,
  clearBreakSheetDraft,
  describeDraftAge,
  draftFootprint,
  draftHasWork,
  draftRemainsStored,
  fieldIsWithheld,
  hasWithheldChoice,
  landSheetsOnKnownTypes,
  noWithheldFields,
  prepareSheetsForPrinting,
  printableSheetFields,
  printAndOpenBreakSheets,
  pruneBreakSheetDrafts,
  restoreBreakSheetDraft,
  sheetIsEmpty,
  sheetTypeProblem,
  sheetValue,
  sheetValuesByName,
  toggleWithheldField,
  withSheetValue,
  writeBreakSheetDraft,
  MAX_FIELD_VALUE_LENGTH,
  type DraftDocumentType,
  type DraftSheet,
  type PrintableDocumentType,
  type WithheldFields,
  type WithholdableField,
} from './break-sheet-draft';
import {
  buildImportPlan,
  parseDelimitedTable,
  suggestMapping,
  type DelimitedTable,
  type ImportDocumentType,
  type ImportMapping,
} from './break-sheet-table';
import { rememberPaper, rememberedPaper } from './break-sheet-paper';

/**
 * The most sheets one run can hold.
 *
 * Not a new number: `MAX_SHEETS_PER_RUN` in the main process has always been
 * 200, and the plain-sheet count already offers all 200. The old cap of 25 here
 * existed to bound how much unsaved typing a stray Escape could destroy, and
 * with the draft persisted that reason is gone — so the renderer states the
 * limit the system actually enforces instead of a lower one of its own, and an
 * operator is stopped before their mapping work is wasted rather than after.
 */
const MAX_SHEETS = 200;

/**
 * How many sheets are on screen at once. Two hundred sheets is up to two
 * thousand text inputs, and a keystroke that re-renders all of them is felt.
 * This is a viewport, not a limit.
 */
const SHEETS_PER_PAGE = 25;

/** Refuse a file that cannot plausibly be a sheet table before reading it all. */
const MAX_TABLE_BYTES = 8 * 1024 * 1024;

type JobDocumentType = JobDefinition['documentTypes'][number];

/** The fields an operator can actually write on a printed sheet (`printableSheetFields`). */
const printableFields = (type: JobDocumentType | undefined) =>
  printableSheetFields(type?.fields ?? []);

/** A type's fields as a sheet files its values under them (`break-sheet-draft`). */
const draftTypeOf = (type: JobDocumentType): DraftDocumentType => ({
  id: type.id,
  fields: type.fields.map<WithholdableField>((field) => ({ id: field.id, name: field.name })),
});

const formatWhen = (iso: string): string => {
  const when = new Date(iso);
  return Number.isNaN(when.getTime()) ? '' : when.toLocaleString();
};

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? '' : 's'}`;

export function BreakSheetsModal({
  job,
  onClose,
  onToast,
}: {
  job: JobDefinition;
  onClose: () => void;
  onToast: (message: string) => void;
}) {
  const dialogRef = useModalFocus();
  const desktop = window.capturePointDesktop;

  const documentTypes = useMemo(
    () => job.documentTypes.filter((type) => type.status !== 'archived'),
    [job.documentTypes],
  );
  const blankSheet = useCallback(
    (): DraftSheet => ({ documentTypeId: documentTypes[0]?.id ?? '', values: {} }),
    [documentTypes],
  );

  // Read once, on the way in. Everything below is seeded from it so a restored
  // draft is the state the dialog opens in rather than something that arrives a
  // frame later and overwrites what the operator has already started typing.
  const [restoredDraft] = useState(() => {
    // Drafts belonging to jobs this workstation no longer has are swept here.
    // Nothing else ever will: deleting a job does not reach this dialog, and the
    // origin quota is shared with everything else the renderer keeps.
    pruneBreakSheetDrafts(window.localStorage, { keepJobId: job.id });
    // Every type the job has today, archived ones included, each with its own
    // fields. The draft's values are filed under these ids; an older draft filed
    // them under the names those fields had when it was written, and this is what
    // remaps them. Grouped by type, because a field name only identifies a field
    // within one type. A job keeps learning between the draft being written and
    // being read: an empty sheet on a type it has since archived lands on a live
    // type, and a filled-in one keeps its type and values and waits for the
    // operator to choose one - landed, it printed under the first type's name
    // with its values dropped. The withheld choice belongs to the whole set, so it
    // comes across untouched.
    return restoreBreakSheetDraft(
      window.localStorage,
      job.id,
      job.documentTypes.map((type) => ({
        ...draftTypeOf(type),
        archived: type.status === 'archived',
      })),
    );
  });
  const [sheets, setSheets] = useState<DraftSheet[]>(
    () => restoredDraft?.sheets ?? [{ documentTypeId: documentTypes[0]?.id ?? '', values: {} }],
  );
  const typeIds = useMemo(() => documentTypes.map((type) => type.id), [documentTypes]);
  // The job keeps learning while this dialog is open: it can gain its first
  // types after a sheet was made with none, or archive the type a sheet is on.
  // An empty sheet is shown with the first type's fields, so that is the type it
  // is put on - otherwise what was typed into those boxes printed as nothing.
  // A sheet already filled in keeps its vanished type: moved, it printed as the
  // first type with none of its fields. It asks for a type instead, and printing
  // is refused until it has one.
  useEffect(() => {
    setSheets((current) => landSheetsOnKnownTypes(current, typeIds, { onlyEmpty: true }));
  }, [typeIds]);
  const [withheldFields, setWithheldFields] = useState<WithheldFields>(
    () => restoredDraft?.withheldFields ?? noWithheldFields,
  );
  const [restoredNotice, setRestoredNotice] = useState(() =>
    restoredDraft && draftHasWork(restoredDraft.sheets) ? restoredDraft : null,
  );

  const [kind, setKind] = useState<'plain' | 'fields'>(() =>
    restoredDraft && draftHasWork(restoredDraft.sheets) && documentTypes.length > 0
      ? 'fields'
      : 'plain',
  );
  const [count, setCount] = useState(10);
  // The paper the PDF is drawn on. The symbols keep their size on both; only the page moves.
  const [paper, setPaper] = useState<BreakSheetPaper>(() =>
    rememberedPaper(window.localStorage, navigator.language),
  );
  const choosePaper = (next: BreakSheetPaper) => {
    setPaper(next);
    rememberPaper(window.localStorage, next);
  };
  const [runs, setRuns] = useState<BreakSheetRun[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saveWarning, setSaveWarning] = useState('');
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [page, setPage] = useState(0);

  /** The types as the print request needs them: names to show, ids to decide by. */
  const printableTypes = useMemo<PrintableDocumentType[]>(
    () =>
      documentTypes.map((type) => ({
        id: type.id,
        name: type.name,
        fields: printableFields(type).map<WithholdableField>((field) => ({
          id: field.id,
          name: field.name,
        })),
      })),
    [documentTypes],
  );

  // This job's history, not the workstation's. A prepared run belongs to the job whose
  // fields it carries values for, and one listed under another job is a reprint that
  // separates documents and then fills nothing in — the pipeline drops every value it
  // holds, silently, because the record names a different job.
  const refresh = () => {
    desktop
      ?.listBreakSheetRuns({ jobId: job.id })
      .then(setRuns)
      .catch(() => undefined);
  };
  useEffect(refresh, [desktop, job.id]);

  // ---------------------------------------------------------------------------
  // The draft
  // ---------------------------------------------------------------------------

  /**
   * Once a job has a stored draft, keep storing it: a set the operator has
   * emptied on purpose has to overwrite what is on disk, not leave the old one
   * behind to be restored next time.
   */
  const persisted = useRef(restoredDraft !== null);
  const lastSavedAt = useRef(0);
  const latest = useRef({ sheets, withheldFields });
  latest.current = { sheets, withheldFields };

  /**
   * The size at which a save last failed for space, or zero.
   *
   * A set that will not fit does not fit any better one keystroke later, and
   * re-serialising hundreds of kilobytes on every keystroke to be told so again
   * is a storm the operator feels while they are still typing. So a failure
   * stops the attempts until the draft is measurably smaller than the one that
   * failed — which is the only change that could make the next one succeed.
   */
  const failedAtFootprint = useRef(0);

  const hasWork = draftHasWork(sheets);
  const hasChoice = hasWithheldChoice(withheldFields);
  const footprint = draftFootprint(sheets);
  const tooLargeToSave = failedAtFootprint.current > 0 && footprint >= failedAtFootprint.current;

  const save = useCallback(() => {
    const warning = writeBreakSheetDraft(
      window.localStorage,
      job.id,
      latest.current.sheets,
      latest.current.withheldFields,
    );
    // A refusal leaves whatever was already stored exactly where it is, so the
    // flag stays raised: an emptied set still has an old draft to overwrite, and
    // reading a refusal as "nothing is stored" is what left it there.
    persisted.current = draftRemainsStored(persisted.current, warning);
    failedAtFootprint.current = warning === null ? 0 : draftFootprint(latest.current.sheets);
    lastSavedAt.current = Date.now();
    setSaveWarning(warning ?? '');
  }, [job.id]);

  useEffect(() => {
    if (!hasWork && !hasChoice && !persisted.current) return undefined;
    if (tooLargeToSave) return undefined;
    // Debounced so a fast typist is not serialising the whole set on every
    // keystroke, but never more than three seconds behind the typing: that
    // interval is the entire exposure a power cut still has.
    const wait = Math.max(0, Math.min(600, lastSavedAt.current + 3_000 - Date.now()));
    const timer = window.setTimeout(save, wait);
    return () => window.clearTimeout(timer);
  }, [sheets, withheldFields, hasWork, hasChoice, tooLargeToSave, save]);

  useEffect(() => {
    // Closing the window is not a React unmount, so the flush has to be hung on
    // the event the browser does give us as well.
    const flush = () => {
      if (
        failedAtFootprint.current > 0 &&
        draftFootprint(latest.current.sheets) >= failedAtFootprint.current
      )
        return;
      if (persisted.current || draftHasWork(latest.current.sheets)) save();
    };
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [save]);

  const discardDraft = () => {
    clearBreakSheetDraft(window.localStorage, job.id);
    persisted.current = false;
    failedAtFootprint.current = 0;
    setSheets([blankSheet()]);
    setWithheldFields(noWithheldFields);
    setRestoredNotice(null);
    setSaveWarning('');
    setPage(0);
  };

  // ---------------------------------------------------------------------------
  // Leaving
  // ---------------------------------------------------------------------------

  const requestClose = useCallback(() => {
    if (busy) return;
    if (kind === 'fields' && draftHasWork(latest.current.sheets)) {
      setConfirmingClose(true);
      return;
    }
    onClose();
  }, [busy, kind, onClose]);

  const closeKeepingDraft = () => {
    save();
    onClose();
  };

  const closeDiscardingDraft = () => {
    clearBreakSheetDraft(window.localStorage, job.id);
    persisted.current = false;
    latest.current = { sheets: [], withheldFields: noWithheldFields };
    onClose();
  };

  // ---------------------------------------------------------------------------
  // Importing a table
  // ---------------------------------------------------------------------------

  type ImportState = {
    readonly fileName: string;
    readonly table: DelimitedTable;
    readonly typeSource: ImportMapping['typeSource'];
    readonly columns: Record<string, number>;
  };
  const [importState, setImportState] = useState<ImportState | null>(null);
  const [importError, setImportError] = useState('');
  const fileInput = useRef<HTMLInputElement | null>(null);

  const importTypes = useMemo<ImportDocumentType[]>(
    () =>
      documentTypes.map((type) => ({
        id: type.id,
        name: type.name,
        fields: printableFields(type).map<WithholdableField>((field) => ({
          id: field.id,
          name: field.name,
        })),
      })),
    [documentTypes],
  );

  /** The types a mapped column can land on: one chosen type, or any of them. */
  const mappedTypes = useMemo<ImportDocumentType[]>(() => {
    if (!importState) return [];
    const source = importState.typeSource;
    return source.kind === 'fixed'
      ? importTypes.filter((type) => type.id === source.documentTypeId)
      : importTypes;
  }, [importState, importTypes]);

  const mappableFieldNames = useMemo(() => {
    const names: string[] = [];
    for (const type of mappedTypes) {
      for (const field of type.fields) if (!names.includes(field.name)) names.push(field.name);
    }
    return names;
  }, [mappedTypes]);

  const importPlan = useMemo(
    () =>
      importState
        ? buildImportPlan(
            importState.table,
            { typeSource: importState.typeSource, columns: importState.columns },
            mappedTypes,
          )
        : null,
    [importState, mappedTypes],
  );

  /** How many rows of the file the quoting rule could not resolve, not how many entries say so. */
  const unparsedRowCount = (importState?.table.unparsedRows ?? []).reduce(
    (total, entry) => total + entry.rows,
    0,
  );

  const replacingDraft = sheets.length === 1 && sheetIsEmpty(sheets[0]);
  const roomForImport = MAX_SHEETS - (replacingDraft ? 0 : sheets.length);
  const importedCount = Math.max(0, Math.min(importPlan?.sheets.length ?? 0, roomForImport));

  const chooseTable = async (file: File) => {
    setImportError('');
    if (file.size > MAX_TABLE_BYTES) {
      setImportError(
        'That file is far larger than a list of break sheets, so it has not been read.',
      );
      return;
    }
    let text: string;
    try {
      text = await file.text();
    } catch {
      setImportError('That file could not be read.');
      return;
    }
    const parsed = parseDelimitedTable(text, MAX_SHEETS);
    if (!parsed.ok) {
      setImportError(parsed.reason);
      return;
    }
    setKind('fields');
    setImportState({
      fileName: file.name,
      table: parsed.table,
      typeSource: { kind: 'fixed', documentTypeId: documentTypes[0]?.id ?? '' },
      columns: suggestMapping(parsed.table.columns, importTypes),
    });
  };

  const applyImport = () => {
    if (!importPlan) return;
    const base = replacingDraft ? [] : sheets;
    const merged = [...base, ...importPlan.sheets.slice(0, roomForImport)];
    setSheets(merged.length > 0 ? merged : [blankSheet()]);
    setPage(0);
    setImportState(null);
    onToast(
      `${plural(merged.length - base.length, 'sheet')} added from ${importState?.fileName ?? 'the table'}.`,
    );
  };

  // ---------------------------------------------------------------------------
  // Printing
  // ---------------------------------------------------------------------------

  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const create = async () => {
    if (!desktop || busy) return;
    // Refused rather than sent: the sheet would print with no type and none of
    // its values, and creating the run clears the draft that holds them.
    const typeProblem = kind === 'fields' ? sheetTypeProblem(sheets, typeIds) : null;
    if (typeProblem) {
      setError(typeProblem);
      return;
    }
    setBusy(true);
    setError('');
    // Creating the run and opening it are separate outcomes. A workstation with
    // no PDF handler throws on the open, and when that shared a `try` with the
    // creation the operator saw no toast, kept their draft, and pressed Print
    // again — minting a second two-hundred-sheet run of the same sheets.
    const outcome = await printAndOpenBreakSheets(
      () =>
        kind === 'plain'
          ? desktop.createPlainBreakSheets({ count, jobId: job.id, paper })
          : desktop.createFieldBreakSheets({
              jobId: job.id,
              sheets: prepareSheetsForPrinting(sheets, printableTypes, withheldFields),
              paper,
            }),
      (run) => desktop.openBreakSheetRun({ runId: run.runId }),
    );
    setBusy(false);

    if (!outcome.created) {
      setError(outcome.error);
      return;
    }
    setError(outcome.openError ?? '');
    onToast(
      outcome.openError === null
        ? `${plural(outcome.run.sheetCount, 'break sheet')} ready to print.`
        : `${plural(outcome.run.sheetCount, 'break sheet')} created. Open them from the list below.`,
    );
    if (kind === 'fields') {
      // The run is durable now — it is in the run history and reprintable — so
      // the draft has served its purpose, whether or not the sheets could be
      // shown. The print choices survive, because the next set of sheets for
      // this job wants the same ones.
      clearBreakSheetDraft(window.localStorage, job.id);
      persisted.current = false;
      failedAtFootprint.current = 0;
      setSheets([blankSheet()]);
      setRestoredNotice(null);
      setSaveWarning('');
      setPage(0);
    }
    refresh();
  };

  const reprint = async (runId: string) => {
    await desktop
      ?.openBreakSheetRun({ runId })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : 'That run could not be opened.'),
      );
  };

  // ---------------------------------------------------------------------------
  // Editing
  // ---------------------------------------------------------------------------

  const updateSheet = (index: number, update: Partial<DraftSheet>) =>
    setSheets((current) =>
      current.map((sheet, position) => (position === index ? { ...sheet, ...update } : sheet)),
    );

  const removeSheet = (index: number) =>
    setSheets((current) => {
      const next = current.filter((_, position) => position !== index);
      return next.length > 0 ? next : [blankSheet()];
    });

  const toggleWithheld = (field: WithholdableField) =>
    setWithheldFields((current) => toggleWithheldField(current, field));

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== 'Escape' ||
        event.defaultPrevented ||
        busy ||
        !isTopVisibleDialog(dialogRef.current)
      )
        return;
      event.preventDefault();
      event.stopImmediatePropagation();
      // Escape steps back out of whatever is innermost, and never past an
      // unanswered question: the first press on a set with typing in it raises
      // the guard, and only the answer closes the dialog.
      if (importState) {
        setImportState(null);
        return;
      }
      if (confirmingClose) {
        closeKeepingDraft();
        return;
      }
      requestClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [busy, confirmingClose, importState, requestClose]);

  const pageCount = Math.max(1, Math.ceil(sheets.length / SHEETS_PER_PAGE));
  const currentPage = Math.min(page, pageCount - 1);
  const firstOnPage = currentPage * SHEETS_PER_PAGE;
  const visibleSheets = sheets.slice(firstOnPage, firstOnPage + SHEETS_PER_PAGE);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && requestClose()}
    >
      <section
        ref={dialogRef}
        tabIndex={-1}
        className="break-sheets-modal"
        role="dialog"
        data-telemetry-dialog="break-sheets"
        aria-modal="true"
        aria-labelledby="break-sheets-title"
      >
        <header className="sim-scanner-head">
          <div>
            <small className="eyebrow">Break sheets</small>
            <h2 id="break-sheets-title">Print break sheets</h2>
            <p>
              Drop a break sheet in front of each document and the stack separates itself, whichever
              way round the sheet is fed. Sheets print as a PDF so the barcode keeps its exact size
              on paper: print it at actual size (100%), not fit to page.
            </p>
          </div>
          <button
            className="icon-button"
            onClick={requestClose}
            disabled={busy}
            aria-label="Close break sheets"
          >
            <X size={17} />
          </button>
        </header>

        <div className="break-sheets-kinds" role="radiogroup" aria-label="Break sheet type">
          <button
            type="button"
            role="radio"
            aria-checked={kind === 'plain'}
            className={`break-sheets-kind ${kind === 'plain' ? 'selected' : ''}`}
            onClick={() => setKind('plain')}
            disabled={busy}
          >
            <strong>Plain sheets</strong>
            <span>
              Identical, reusable, carry no values. Print a stack once and keep it by the scanner.
            </span>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={kind === 'fields'}
            className={`break-sheets-kind ${kind === 'fields' ? 'selected' : ''}`}
            onClick={() => setKind('fields')}
            disabled={busy || documentTypes.length === 0}
          >
            <strong>Sheets with values</strong>
            {/* An option that is simply greyed out reads as a feature that is
                missing or broken. It is neither: it needs fields to print, and this
                job has not learned any yet. Say that, and say what produces them. */}
            <span>
              {documentTypes.length === 0
                ? 'Needs document types and fields. This job has not learned any yet - scan a few pages, or set them up in Job setup, and this becomes available.'
                : 'Each sheet is prepared for one document and carries its field values. Used once.'}
            </span>
          </button>
        </div>

        <div className="break-sheets-kinds" role="radiogroup" aria-label="Paper">
          {(['letter', 'a4'] as const).map((size) => (
            <button
              key={size}
              type="button"
              role="radio"
              aria-checked={paper === size}
              className={`break-sheets-kind ${paper === size ? 'selected' : ''}`}
              onClick={() => choosePaper(size)}
              disabled={busy}
            >
              <strong>{size === 'letter' ? 'Letter' : 'A4'}</strong>
              <span>{size === 'letter' ? '8.5 x 11 in' : '210 x 297 mm'}</span>
            </button>
          ))}
        </div>

        <div className="break-sheets-body">
          {kind === 'plain' ? (
            <label className="break-sheets-count">
              <span>How many sheets</span>
              <input
                type="number"
                min={1}
                max={MAX_SHEETS}
                value={count}
                disabled={busy}
                onChange={(event) =>
                  setCount(Math.max(1, Math.min(MAX_SHEETS, Number(event.target.value) || 1)))
                }
              />
              <small>One page each. They never expire and can be reused indefinitely.</small>
            </label>
          ) : documentTypes.length === 0 ? (
            <p className="break-sheets-empty">
              <TriangleAlert size={15} /> This job has no document types yet, so there are no fields
              to put on a sheet. Scan or import some pages first.
            </p>
          ) : (
            <>
              {restoredNotice && (
                <div className="break-sheets-restored">
                  <RotateCcw size={15} />
                  <span>
                    <strong>Your draft is back.</strong>{' '}
                    {plural(restoredNotice.sheets.length, 'sheet')} you were preparing
                    {describeDraftAge(restoredNotice.savedAt)
                      ? ` ${describeDraftAge(restoredNotice.savedAt)}`
                      : ''}
                    . Nothing has been printed yet.
                  </span>
                  <button
                    type="button"
                    className="quiet-button"
                    onClick={discardDraft}
                    disabled={busy}
                  >
                    Start over
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => setRestoredNotice(null)}
                    aria-label="Dismiss"
                  >
                    <X size={15} />
                  </button>
                </div>
              )}

              {saveWarning && (
                <div className="break-sheets-warning">
                  <TriangleAlert size={15} /> {saveWarning}
                </div>
              )}

              <div className="break-sheets-toolbar">
                <span>
                  {plural(sheets.length, 'sheet')} in this set
                  {sheets.length >= MAX_SHEETS ? ' - the most one run can hold' : ''}.
                </span>
                <button
                  type="button"
                  className="quiet-button"
                  disabled={busy}
                  onClick={() => fileInput.current?.click()}
                >
                  <FileSpreadsheet size={15} /> Import a table…
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  accept=".csv,.tsv,.tab,.txt,text/csv,text/tab-separated-values,text/plain"
                  style={{ display: 'none' }}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    // Cleared so choosing the same file twice fires again, which
                    // is exactly what happens after a mapping is abandoned.
                    event.target.value = '';
                    if (file) void chooseTable(file);
                  }}
                />
              </div>

              {importError && (
                <div className="break-sheets-warning">
                  <TriangleAlert size={15} /> {importError}
                </div>
              )}

              {importState && importPlan && (
                <div className="break-sheets-import">
                  <div className="break-sheets-import-head">
                    <div>
                      <strong>{importState.fileName}</strong>
                      <small>
                        Read as a {importState.table.delimiterName}-separated file:{' '}
                        {plural(importState.table.rows.length, 'row')},{' '}
                        {plural(importState.table.columns.length, 'column')}.
                        {importState.table.droppedRows > 0
                          ? ` ${plural(importState.table.droppedRows, 'further row')} ignored - one run holds ${MAX_SHEETS} sheets.`
                          : ''}
                      </small>
                      {/* A row the quoting rule cannot resolve is named, never
                          folded into its neighbour and never dropped in silence:
                          both of those put values on a sheet that belong to a
                          different document. */}
                      {/* Counted in rows of the file, not in entries: one unclosed
                          quote is one entry and every row after it. */}
                      {unparsedRowCount > 0 && (
                        <small className="break-sheets-import-warn">
                          {plural(unparsedRowCount, 'row')} could not be read -{' '}
                          {importState.table.unparsedRows[0].reason}. Nothing from{' '}
                          {unparsedRowCount > 1 ? 'those rows' : 'that row'} has been used.
                        </small>
                      )}
                      {/* A row with a different column count than the header is
                          padded or cut so a column index still resolves, which
                          means the values it does carry may have come from a
                          different column. Say so rather than print a shifted
                          sheet that looks correct. */}
                      {importState.table.raggedRows > 0 && (
                        <small className="break-sheets-import-warn">
                          {plural(importState.table.raggedRows, 'row')}{' '}
                          {importState.table.raggedRows > 1 ? 'have' : 'has'} a different number of
                          columns than the heading
                          {importState.table.firstRaggedLine === null
                            ? ''
                            : `, starting at line ${importState.table.firstRaggedLine}`}
                          . Check those sheets before printing - a value may have been read from the
                          wrong column.
                        </small>
                      )}
                    </div>
                    <button
                      type="button"
                      className="icon-button"
                      onClick={() => setImportState(null)}
                      aria-label="Cancel the import"
                    >
                      <X size={15} />
                    </button>
                  </div>

                  <div className="break-sheets-import-type">
                    <label>
                      <span>Document type</span>
                      <select
                        value={
                          importState.typeSource.kind === 'fixed'
                            ? `fixed:${importState.typeSource.documentTypeId}`
                            : `column:${importState.typeSource.column}`
                        }
                        onChange={(event) => {
                          const chosen = event.target.value;
                          setImportState((current) => {
                            if (!current) return current;
                            const typeSource: ImportMapping['typeSource'] = chosen.startsWith(
                              'fixed:',
                            )
                              ? { kind: 'fixed', documentTypeId: chosen.slice('fixed:'.length) }
                              : { kind: 'column', column: Number(chosen.slice('column:'.length)) };
                            return { ...current, typeSource };
                          });
                        }}
                      >
                        {documentTypes.map((type) => (
                          <option value={`fixed:${type.id}`} key={`fixed-${type.id}`}>
                            Every row is a {type.name}
                          </option>
                        ))}
                        {importState.table.columns.map((column, index) => (
                          <option value={`column:${index}`} key={`column-${index}`}>
                            The “{column}” column names the type
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="break-sheets-import-map">
                    {mappableFieldNames.length === 0 ? (
                      <p className="break-sheets-empty">
                        That document type has no fields that can be written on a sheet.
                      </p>
                    ) : (
                      mappableFieldNames.map((fieldName) => (
                        <label key={fieldName}>
                          <span>{fieldName}</span>
                          <select
                            value={importState.columns[fieldName] ?? -1}
                            onChange={(event) =>
                              setImportState(
                                (current) =>
                                  current && {
                                    ...current,
                                    columns: {
                                      ...current.columns,
                                      [fieldName]: Number(event.target.value),
                                    },
                                  },
                              )
                            }
                          >
                            <option value={-1}>Leave blank</option>
                            {importState.table.columns.map((column, index) => (
                              <option value={index} key={index}>
                                {column}
                              </option>
                            ))}
                          </select>
                        </label>
                      ))
                    )}
                  </div>

                  {/* The preview is built by the same function that builds the
                      sheets, so what is agreed to here is what gets made. */}
                  <div className="break-sheets-import-preview">
                    <span className="eyebrow">First sheets this makes</span>
                    <table>
                      <tbody>
                        {importPlan.sheets.slice(0, 4).map((sheet, index) => {
                          const type = documentTypes.find(
                            (candidate) => candidate.id === sheet.documentTypeId,
                          );
                          const shown = printableFields(type)
                            .map((field) => ({ name: field.name, value: sheetValue(sheet, field) }))
                            .filter((entry) => entry.value.length > 0)
                            .slice(0, 3);
                          return (
                            <tr key={index}>
                              <th>{type?.name ?? '-'}</th>
                              <td>
                                {shown.length === 0 ? (
                                  <em>nothing mapped</em>
                                ) : (
                                  shown
                                    .map((entry) => `${entry.name}: ${entry.value}`)
                                    .join('  ·  ')
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {importPlan.sheets.length === 0 && (
                          <tr>
                            <td>
                              <em>
                                No rows produced a sheet. Check the document type and the column
                                mapping above.
                              </em>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    {importPlan.unrecognisedTypeRows.length > 0 && (
                      <small className="break-sheets-import-warn">
                        {plural(importPlan.unrecognisedTypeRows.length, 'row')} skipped - no
                        document type in this job is named “
                        {importPlan.unrecognisedTypeRows[0].value || '(blank)'}”
                        {importPlan.unrecognisedTypeRows.length > 1 ? ' and others' : ''} (first at
                        line {importPlan.unrecognisedTypeRows[0].row}).
                      </small>
                    )}
                    {importPlan.emptyRows.length > 0 && (
                      <small className="break-sheets-import-warn">
                        {plural(importPlan.emptyRows.length, 'row')} would make a sheet with no
                        values on it (first at line {importPlan.emptyRows[0]}).
                      </small>
                    )}
                    {importPlan.shortenedValues > 0 && (
                      <small className="break-sheets-import-warn">
                        {importPlan.shortenedValues === 1
                          ? 'One value was'
                          : `${importPlan.shortenedValues} values were`}{' '}
                        longer than a break sheet field holds, and cut to {MAX_FIELD_VALUE_LENGTH}{' '}
                        characters.
                      </small>
                    )}
                    {importPlan.sheets.length > roomForImport && (
                      <small className="break-sheets-import-warn">
                        Only {plural(roomForImport, 'sheet')} will fit alongside what is already
                        here; the rest will not be added.
                      </small>
                    )}
                  </div>

                  <div className="break-sheets-import-actions">
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => setImportState(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="primary-button"
                      disabled={importedCount === 0}
                      onClick={applyImport}
                    >
                      {replacingDraft
                        ? `Prepare ${plural(importedCount, 'sheet')}`
                        : `Add ${plural(importedCount, 'sheet')}`}
                    </button>
                  </div>
                </div>
              )}

              {!importState &&
                visibleSheets.map((sheet, positionOnPage) => {
                  const index = firstOnPage + positionOnPage;
                  const offered = documentTypes.find(
                    (candidate) => candidate.id === sheet.documentTypeId,
                  );
                  // An empty sheet on a missing type is about to land on the first
                  // type (the effect above); a filled-in one waits for a choice.
                  const type = offered ?? (sheetIsEmpty(sheet) ? documentTypes[0] : undefined);
                  const needsType = !type && documentTypes.length > 0;
                  // The type it was on, archived but still in the job: its name, and
                  // its fields for carrying the values over by name.
                  const vanished = needsType
                    ? job.documentTypes.find((candidate) => candidate.id === sheet.documentTypeId)
                    : undefined;
                  const fields = printableFields(type);
                  const waitingValues = vanished
                    ? sheetValuesByName(sheet, draftTypeOf(vanished))
                    : [];
                  const chooseType = (chosenId: string) => {
                    // Carried only into fields the sheet can show and print.
                    const chosen = printableTypes.find((candidate) => candidate.id === chosenId);
                    updateSheet(
                      index,
                      needsType && chosen
                        ? carrySheetToType(sheet, vanished && draftTypeOf(vanished), chosen)
                        : { documentTypeId: chosenId, values: {} },
                    );
                  };
                  return (
                    <div className="break-sheets-draft" key={index}>
                      <div className="break-sheets-draft-head">
                        <label>
                          <span>Document type - sheet {index + 1}</span>
                          <select
                            value={needsType ? '' : (type?.id ?? sheet.documentTypeId)}
                            disabled={busy}
                            onChange={(event) => chooseType(event.target.value)}
                          >
                            {needsType && (
                              <option value="" disabled>
                                Choose a document type
                              </option>
                            )}
                            {documentTypes.map((candidate) => (
                              <option value={candidate.id} key={candidate.id}>
                                {candidate.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        {sheets.length > 1 && (
                          <TooltipButton
                            className="icon-button"
                            disabled={busy}
                            aria-label={`Remove sheet ${index + 1}`}
                            tooltip="Remove sheet"
                            onClick={() => removeSheet(index)}
                          >
                            <Trash2 size={15} />
                          </TooltipButton>
                        )}
                      </div>
                      <div className="break-sheets-fields">
                        {fields.map((field) => {
                          const withheld = fieldIsWithheld(withheldFields, field);
                          return (
                            <label key={field.name} className={withheld ? 'withheld' : ''}>
                              <span>
                                {field.name}
                                {/* The choice belongs to the field, not to this one
                                  sheet and not to this one document type: an SSN
                                  is an SSN on all two hundred sheets and on every
                                  kind of document in the set. Asking again per
                                  sheet would be the data entry this dialog exists
                                  to remove; asking again per type would leave the
                                  Referrals printing what the Claims withheld. */}
                                <button
                                  type="button"
                                  className="break-sheets-withhold"
                                  disabled={busy}
                                  onClick={() => toggleWithheld(field)}
                                  aria-pressed={withheld}
                                  aria-label={
                                    withheld
                                      ? `Print ${field.name}`
                                      : `Leave ${field.name} off the sheets`
                                  }
                                  title={withheld ? 'Print it' : 'Leave it off the sheets'}
                                >
                                  {withheld ? <EyeOff size={13} /> : <Eye size={13} />}
                                  {withheld ? 'not printed' : ''}
                                </button>
                              </span>
                              <input
                                value={sheetValue(sheet, field)}
                                disabled={busy}
                                maxLength={MAX_FIELD_VALUE_LENGTH}
                                placeholder={field.formatExample ?? ''}
                                onChange={(event) =>
                                  updateSheet(index, {
                                    // The type whose fields these boxes are, so the
                                    // value prints on the sheet it was typed on.
                                    documentTypeId: type?.id ?? sheet.documentTypeId,
                                    values: withSheetValue(
                                      sheet,
                                      field,
                                      event.target.value.slice(0, MAX_FIELD_VALUE_LENGTH),
                                    ).values,
                                  })
                                }
                              />
                            </label>
                          );
                        })}
                        {needsType && (
                          <div className="break-sheets-waiting">
                            <p className="break-sheets-empty">
                              {vanished
                                ? `“${vanished.name}” is no longer a document type in this job.`
                                : "This sheet's document type is no longer in this job."}{' '}
                              Choose a document type for this sheet
                              {vanished
                                ? '; what was typed carries over to fields with the same name.'
                                : '.'}
                            </p>
                            {/* Its boxes are gone with its type, so what it holds is
                              shown here, read-only, until a type is chosen. */}
                            {waitingValues.length > 0 && (
                              <p className="break-sheets-waiting-values">
                                {waitingValues
                                  .map((entry) => `${entry.name}: ${entry.value}`)
                                  .join('  ·  ')}
                              </p>
                            )}
                          </div>
                        )}
                        {!needsType && fields.length === 0 && (
                          <p className="break-sheets-empty">
                            This type has no fields that can be written on a sheet.
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}

              {!importState && pageCount > 1 && (
                <div className="break-sheets-pager">
                  <button
                    type="button"
                    className="quiet-button"
                    disabled={currentPage === 0}
                    onClick={() => setPage(currentPage - 1)}
                  >
                    <ChevronLeft size={15} /> Earlier
                  </button>
                  <span>
                    Sheets {firstOnPage + 1}–
                    {Math.min(sheets.length, firstOnPage + SHEETS_PER_PAGE)} of {sheets.length}
                  </span>
                  <button
                    type="button"
                    className="quiet-button"
                    disabled={currentPage >= pageCount - 1}
                    onClick={() => setPage(currentPage + 1)}
                  >
                    Later <ChevronRight size={15} />
                  </button>
                </div>
              )}

              {!importState && (
                <button
                  className="quiet-button"
                  disabled={busy || sheets.length >= MAX_SHEETS}
                  onClick={() => {
                    setSheets((current) => [...current, blankSheet()]);
                    setPage(Math.floor(sheets.length / SHEETS_PER_PAGE));
                  }}
                >
                  <Plus size={15} /> Another sheet
                </button>
              )}
            </>
          )}

          {runs.length > 0 && (
            <div className="break-sheets-history">
              <span className="eyebrow">Printed before</span>
              {runs.slice(0, 6).map((run) => (
                <button
                  className="break-sheets-run"
                  key={run.runId}
                  onClick={() => void reprint(run.runId)}
                  disabled={busy}
                >
                  <FileText size={15} />
                  <span>
                    {plural(
                      run.sheetCount,
                      run.kind === 'plain' ? 'plain sheet' : 'prepared sheet',
                    )}
                  </span>
                  <small>{formatWhen(run.createdAt)}</small>
                </button>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div className="modal-error">
            <TriangleAlert size={15} /> {error}
          </div>
        )}

        {confirmingClose ? (
          <div className="break-sheets-confirm">
            <TriangleAlert size={16} />
            <span>
              <strong>{plural(sheets.length, 'sheet')} prepared and not printed.</strong> The draft
              is kept for this job and comes back when you reopen this window, unless you throw it
              away now.
            </span>
            <button
              type="button"
              className="quiet-button"
              onClick={() => setConfirmingClose(false)}
            >
              Keep editing
            </button>
            <button type="button" className="quiet-button danger" onClick={closeDiscardingDraft}>
              Discard it
            </button>
            <button type="button" className="secondary-button" onClick={closeKeepingDraft}>
              Close, keep the draft
            </button>
          </div>
        ) : (
          <footer className="modal-actions">
            <button className="secondary-button" onClick={requestClose} disabled={busy}>
              Close
            </button>
            <button
              className="primary-button"
              onClick={() => void create()}
              disabled={
                busy || Boolean(importState) || (kind === 'fields' && documentTypes.length === 0)
              }
            >
              {busy ? <LoaderCircle className="spin" size={15} /> : <Printer size={16} />}
              {busy
                ? 'Preparing…'
                : kind === 'plain'
                  ? `Create ${plural(count, 'sheet')}`
                  : `Create ${plural(sheets.length, 'sheet')}`}
            </button>
          </footer>
        )}
      </section>
    </div>
  );
}
